import { useEffect, useRef, useState } from 'react'
import { MessageSquareWarning, Plus, RefreshCw, RotateCcw, Search, SlidersHorizontal, X } from 'lucide-react'
import { Link, navigate } from '../router.js'
import { Trail } from '../navigation/Trail.js'
import { SmartBack } from '../navigation/SmartBack.js'
import { Select, type SelectOption } from '../admin/primitives.js'
import { SegmentedControl } from '../admin/pickers.js'
import { remplacerRecherche } from '../finance/adresse-ecran.js'
import { ilYA } from '../finance/ClosingCommon.js'
import { chargerListe, chargerMoi, chargerResponsables, chargerSynthese } from './signalements-api.js'
import {
  MESSAGE_LISTE_ACTUALISEE, ajouterPage, cheminFiche, compte, dateHeureParis, filtresActifs,
  filtresDepuisUrl, filtresVersUrl, FILTRES_DEFAUT, libelle, limite, memesFiltres, nomDe,
  requeteListe, statutsDeLEtat, suiteApresErreur, tonDe,
  type Droits, type ErreurSig, type Etat, type Filtres, type FiltreResponsable, type MetaSignalements,
  type Responsable, type Resume, type Synthese, type Tri,
} from './signalements-model.js'
import { Chargement, EtatErreur, Pastille, Point, useMetaSignalements } from './SignalementsCommun.js'

/**
 * SIGNALEMENTS — LA LISTE (`/technique/signalements`, 09/10/2026).
 *
 * ═══ TECHNIQUE EST LA SOURCE DE VÉRITÉ ═══
 * Les compteurs du bandeau sont ceux de Technique (`/summary`), jamais
 * recalculés depuis la page affichée. Les filtres sont CEUX que l'API sait
 * appliquer, avec ses propres noms de paramètres : l'adresse les porte (un
 * lien envoyé à un collègue rouvre la même liste), en remplaçant l'entrée
 * d'historique (`remplacerRecherche`) — le Retour quitte la liste en un clic,
 * et depuis une fiche il la retrouve filtrée.
 *
 * La pagination suit un curseur OPAQUE, gardé dans l'écran le temps de
 * « Charger plus » et nulle part ailleurs. Un curseur que Technique refuse
 * (`INVALID_CURSOR` : redémarrage, filtres changés) relance la première page,
 * avec un message discret.
 */

const CHEMIN = '/technique/signalements'

export function SignalementsListePage() {
  const { etat, recharger } = useMetaSignalements()
  return (
    <main className="lc-page lc-page--management lc-stagger sig-page">
      <Trail />
      <SmartBack />
      {etat.phase === 'chargement' ? (
        <>
          <EnTete droits={null} />
          <Chargement libelle="Chargement des signalements" />
        </>
      ) : etat.phase === 'erreur' ? (
        <>
          <EnTete droits={null} />
          <EtatErreur erreur={etat.erreur} quoi="les signalements" onReessayer={recharger} />
        </>
      ) : (
        <Liste meta={etat.meta} droits={etat.droits} />
      )}
    </main>
  )
}

function EnTete({ droits }: { droits: Droits | null }) {
  return (
    <header className="sig-hero">
      <div>
        <h1 className="lc-title"><MessageSquareWarning size={26} aria-hidden="true" /> Signalements</h1>
        <p className="lc-subtitle">
          Les problèmes techniques signalés, du constat à la résolution. Suivis par
          l’équipe Technique, qui en garde l’historique complet.
        </p>
      </div>
      {droits?.create ? (
        <div className="sig-hero__actions">
          <Link href={`${CHEMIN}/nouveau`} className="lc-btn lc-btn--primary">
            <Plus size={16} aria-hidden="true" /> Nouveau signalement
          </Link>
        </div>
      ) : null}
    </header>
  )
}

/* ═══════════════════════ le bandeau de synthèse ═══════════════════════ */

function Bandeau(props: {
  synthese: Synthese | null
  erreur: ErreurSig | null
  meta: MetaSignalements
  onFiltre: (f: Partial<Filtres>) => void
}) {
  const s = props.synthese
  if (props.erreur) return <EtatErreur erreur={props.erreur} quoi="la synthèse" compact />
  const val = (n: number | undefined) => (n === undefined ? '…' : String(n))
  const cases: { cle: string; libelle: string; valeur: string; filtre: Partial<Filtres>; ton?: string }[] = [
    { cle: 'ouverts', libelle: 'Ouverts', valeur: val(s?.ouverts), filtre: { state: 'active' } },
    { cle: 'nouveaux', libelle: libelle(props.meta.statuts, 'nouveau'), valeur: val(s?.nouveaux), filtre: { state: 'active', status: ['nouveau'] }, ton: tonDe(props.meta.statuts, 'nouveau') },
    { cle: 'pris', libelle: libelle(props.meta.statuts, 'pris_en_charge'), valeur: val(s?.pris_en_charge), filtre: { state: 'active', status: ['pris_en_charge'] }, ton: tonDe(props.meta.statuts, 'pris_en_charge') },
    { cle: 'attente', libelle: libelle(props.meta.statuts, 'en_attente'), valeur: val(s?.en_attente), filtre: { state: 'active', status: ['en_attente'] }, ton: tonDe(props.meta.statuts, 'en_attente') },
    { cle: 'elevee', libelle: 'Priorité élevée', valeur: val(s?.priorite_elevee), filtre: { state: 'active', priority: props.meta.priorites_elevees }, ton: 'danger' },
    { cle: 'non_affectes', libelle: 'Non affectés', valeur: val(s?.non_affectes), filtre: { state: 'active', responsable: { type: 'non_affecte' } }, ton: 'warning' },
  ]
  return (
    <section className="sig-bandeau" aria-label="Synthèse des signalements">
      <ul className="sig-bandeau__cases">
        {cases.map((c) => (
          <li key={c.cle}>
            <button type="button" className="sig-case" onClick={() => props.onFiltre(c.filtre)}
              aria-label={`${c.libelle} : ${c.valeur}. Afficher ces signalements.`}>
              <span className="sig-case__valeur">{c.valeur}</span>
              <span className="sig-case__libelle">
                {c.ton ? <Point ton={c.ton} /> : null}
                {c.libelle}
              </span>
            </button>
          </li>
        ))}
      </ul>
      {/* Sans signalement ouvert, rien : le compteur dit déjà 0, et l'état vide de la liste le dit en toutes lettres. */}
      {s === null ? <p className="sig-bandeau__ancien">…</p>
        : s.plus_ancien_ouvert ? (
          <p className="sig-bandeau__ancien">
            Plus ancien ouvert : <Link href={cheminFiche(s.plus_ancien_ouvert.reference)} className="sig-lien">{s.plus_ancien_ouvert.reference}</Link>
            {' '}— {s.plus_ancien_ouvert.titre || 'sans titre'}, signalé {ilYA(s.plus_ancien_ouvert.created_at)}
          </p>
        ) : null}
    </section>
  )
}

/* ═══════════════════════ la liste ═══════════════════════ */

type EtatListe =
  | { phase: 'chargement' }
  | { phase: 'ok'; items: Resume[]; curseur: string | null; encore: boolean }
  | { phase: 'erreur'; erreur: ErreurSig }

function lireAdresse(meta: MetaSignalements): Filtres {
  return filtresDepuisUrl(new URLSearchParams(window.location.search), meta, limite(meta, 'recherche'))
}

function Liste({ meta, droits }: { meta: MetaSignalements; droits: Droits }) {
  const [filtres, setFiltres] = useState<Filtres>(() => lireAdresse(meta))
  const [saisie, setSaisie] = useState(filtres.q)
  const [liste, setListe] = useState<EtatListe>({ phase: 'chargement' })
  const [suiteEnCours, setSuiteEnCours] = useState(false)
  const [suiteErreur, setSuiteErreur] = useState<ErreurSig | null>(null)
  const [avis, setAvis] = useState<string | null>(null)
  const [synthese, setSynthese] = useState<Synthese | null>(null)
  const [syntheseErreur, setSyntheseErreur] = useState<ErreurSig | null>(null)
  const [responsables, setResponsables] = useState<Responsable[]>([])
  const [moi, setMoi] = useState<string | null>(null)
  const [plus, setPlus] = useState(() => filtres.status.length + filtres.priority.length + filtres.impact.length
    + filtres.category.length + filtres.source.length > 0 || filtres.responsable.type !== 'tous')
  const [rafraichir, setRafraichir] = useState(0)
  const generation = useRef(0)
  const taille = limite(meta, 'page_defaut')

  // L'adresse dit exactement ce que la liste montre : valeurs inconnues retirées dès l'arrivée.
  useEffect(() => { remplacerRecherche(CHEMIN, filtresVersUrl(filtres)) }, [filtres])

  // Recherche temporisée : une requête par pause de frappe, pas par touche.
  useEffect(() => {
    const t = window.setTimeout(() => {
      const q = saisie.trim().slice(0, limite(meta, 'recherche'))
      setFiltres((f) => (f.q === q ? f : { ...f, q }))
    }, 300)
    return () => window.clearTimeout(t)
  }, [saisie, meta])

  // Première page : à chaque changement de filtres, sans curseur.
  useEffect(() => {
    generation.current += 1
    const g = generation.current
    setListe({ phase: 'chargement' })
    setSuiteErreur(null)
    void chargerListe(requeteListe(filtres, taille)).then((r) => {
      if (g !== generation.current) return
      setListe(r.ok ? { phase: 'ok', items: r.data.items, curseur: r.data.next_cursor, encore: r.data.has_more } : { phase: 'erreur', erreur: r.erreur })
    })
  }, [filtres, taille, rafraichir])

  useEffect(() => {
    let vivant = true
    setSynthese(null)
    void chargerSynthese().then((r) => {
      if (!vivant) return
      if (r.ok) { setSynthese(r.data); setSyntheseErreur(null) } else setSyntheseErreur(r.erreur)
    })
    return () => { vivant = false }
  }, [rafraichir])

  useEffect(() => {
    let vivant = true
    void chargerMoi().then((m) => { if (vivant) setMoi(m?.id ?? null) })
    if (droits.manage) void chargerResponsables().then((r) => { if (vivant && r.ok) setResponsables(r.data) })
    return () => { vivant = false }
  }, [droits.manage])

  const changer = (patch: Partial<Filtres>) => {
    setAvis(null)
    setFiltres((f) => {
      const n = { ...f, ...patch }
      // Un statut qui n'appartient plus à l'état choisi tombe.
      if (patch.state && !patch.status) {
        const permis = statutsDeLEtat(meta, patch.state).map((s) => s.code)
        n.status = f.status.filter((s) => permis.includes(s))
      }
      return memesFiltres(n, f) ? f : n
    })
  }

  /** Une case du bandeau montre EXACTEMENT ce qu'elle compte : les autres filtres tombent. */
  const appliquerCase = (patch: Partial<Filtres>) => {
    setSaisie('')
    setAvis(null)
    setFiltres((f) => ({ ...FILTRES_DEFAUT, sort: f.sort, ...patch }))
  }

  const reinitialiser = () => { setSaisie(''); setAvis(null); setFiltres({ ...FILTRES_DEFAUT, sort: filtres.sort }) }

  const chargerPlus = async () => {
    if (liste.phase !== 'ok' || !liste.curseur) return
    const g = generation.current
    setSuiteEnCours(true)
    setSuiteErreur(null)
    const r = await chargerListe(requeteListe(filtres, taille, liste.curseur))
    if (g !== generation.current) return
    setSuiteEnCours(false)
    if (r.ok) {
      setListe({ phase: 'ok', items: ajouterPage(liste.items, r.data.items), curseur: r.data.next_cursor, encore: r.data.has_more })
      return
    }
    if (suiteApresErreur(r.erreur) === 'recommencer') {
      // Le curseur ne vaut plus rien : on repart de la première page, sans bruit.
      setAvis(MESSAGE_LISTE_ACTUALISEE)
      setRafraichir((n) => n + 1)
      return
    }
    setSuiteErreur(r.erreur)
  }

  const actualiser = () => { setAvis(null); setRafraichir((n) => n + 1) }

  return (
    <>
      <EnTete droits={droits} />
      <Bandeau synthese={synthese} erreur={syntheseErreur} meta={meta} onFiltre={appliquerCase} />

      <section className="sig-filtres" aria-label="Filtres de la liste">
        <div className="sig-filtres__ligne">
          <label className="lc-search sig-filtres__recherche">
            <Search size={16} className="lc-search__icon" aria-hidden="true" />
            <input type="search" value={saisie} onChange={(e) => setSaisie(e.target.value)}
              placeholder="Référence, titre, description, emplacement…" aria-label="Rechercher un signalement"
              maxLength={limite(meta, 'recherche')} />
          </label>
          <SegmentedControl<Etat> value={filtres.state} ariaLabel="État des signalements"
            options={[{ value: 'active', label: 'Ouverts' }, { value: 'closed', label: 'Fermés' }, { value: 'all', label: 'Tous' }]}
            onChange={(v) => changer({ state: v })} />
          <SegmentedControl<Tri> value={filtres.sort} ariaLabel="Ordre de la liste" size="sm"
            options={[{ value: 'priority', label: 'Priorité' }, { value: 'recent', label: 'Récents' }]}
            onChange={(v) => changer({ sort: v })} />
          <button type="button" className={`lc-btn lc-btn--ghost sig-filtres__plus${plus ? ' sig-filtres__plus--on' : ''}`}
            aria-expanded={plus} aria-controls="sig-filtres-avances" onClick={() => setPlus((p) => !p)}>
            <SlidersHorizontal size={16} aria-hidden="true" /> Filtres
          </button>
          <span className="sig-espace" />
          <button type="button" className="lc-btn lc-btn--ghost lc-btn--icon" onClick={actualiser} aria-label="Actualiser la liste et la synthèse">
            <RefreshCw size={16} aria-hidden="true" />
          </button>
        </div>

        {plus ? (
          <div className="sig-filtres__avances" id="sig-filtres-avances">
            <FiltreCatalogue label="Statut" valeurs={filtres.status} options={statutsDeLEtat(meta, filtres.state)} onChange={(v) => changer({ status: v })} />
            <FiltreCatalogue label="Priorité" valeurs={filtres.priority} options={meta.priorites} onChange={(v) => changer({ priority: v })} />
            <FiltreCatalogue label="Impact" valeurs={filtres.impact} options={meta.impacts} onChange={(v) => changer({ impact: v })} />
            <FiltreCatalogue label="Catégorie" valeurs={filtres.category} options={meta.categories} onChange={(v) => changer({ category: v })} />
            {meta.sources.length > 0 ? (
              <FiltreCatalogue label="Source" valeurs={filtres.source} options={meta.sources} onChange={(v) => changer({ source: v })} />
            ) : null}
            <FiltreResponsableSelect valeur={filtres.responsable} moi={moi} responsables={responsables} onChange={(v) => changer({ responsable: v })} />
          </div>
        ) : null}

        {filtres.objet || filtresActifs(filtres) ? (
          <div className="sig-filtres__ligne">
            {filtres.objet ? (
              <span className="sig-puce">
                <span className="sig-puce__cle">Lié à</span>
                <span className="sig-puce__val">
                  {filtres.objet.domain === 'parc' && filtres.objet.entity_type === 'item'
                    ? `l’équipement ${filtres.objet.entity_id} du Parc`
                    : `${filtres.objet.domain} · ${filtres.objet.entity_type} ${filtres.objet.entity_id}`}
                </span>
                <button type="button" className="sig-puce__retirer" onClick={() => changer({ objet: null })}
                  aria-label="Retirer le filtre d’objet lié">
                  <X size={14} aria-hidden="true" />
                </button>
              </span>
            ) : null}
            {filtresActifs(filtres) ? (
              <button type="button" className="lc-btn lc-btn--sm lc-btn--ghost" onClick={reinitialiser}>
                <RotateCcw size={14} aria-hidden="true" /> Réinitialiser les filtres
              </button>
            ) : null}
          </div>
        ) : null}
      </section>

      {avis ? <p className="sig-avis" role="status">{avis}</p> : null}

      {liste.phase === 'chargement' ? <Chargement libelle="Chargement de la liste" /> : null}
      {liste.phase === 'erreur' ? <EtatErreur erreur={liste.erreur} quoi="la liste" onReessayer={actualiser} /> : null}
      {liste.phase === 'ok' && liste.items.length === 0 ? (
        <div className="sig-vide">
          <p className="sig-vide__titre">
            {filtresActifs(filtres) ? 'Aucun signalement ne correspond à ces filtres'
              : filtres.state === 'closed' ? 'Aucun signalement fermé' : filtres.state === 'all' ? 'Aucun signalement' : 'Aucun signalement ouvert'}
          </p>
          <p>
            {filtresActifs(filtres) ? 'Élargissez la recherche, retirez un filtre ou réinitialisez-les.'
              : 'Tout ce que l’équipe signale apparaîtra ici, avec son suivi.'}
          </p>
          {/* Filtres actifs : « Réinitialiser les filtres » est déjà sous la barre de filtres, il n'est pas répété ici. */}
          {filtresActifs(filtres) ? null : droits.create ? (
            <Link href={`${CHEMIN}/nouveau`} className="lc-btn lc-btn--sm"><Plus size={14} aria-hidden="true" /> Nouveau signalement</Link>
          ) : null}
        </div>
      ) : null}

      {liste.phase === 'ok' && liste.items.length > 0 ? (
        <>
          {/* Le choix table / cartes suit la largeur RÉELLE de la zone (requête de conteneur), pas celle de l'écran. */}
          <div className="sig-liste">
            <Table items={liste.items} meta={meta} />
            <Cartes items={liste.items} meta={meta} />
          </div>
          <div className="sig-suite">
            <span className="sig-aide">
              {compte(liste.items.length, 'signalement affiché', 'signalements affichés')}{liste.encore ? ', d’autres suivent' : ''}
            </span>
            {suiteErreur ? <EtatErreur erreur={suiteErreur} compact onReessayer={() => void chargerPlus()} /> : null}
            {liste.encore && !suiteErreur ? (
              <button type="button" className="lc-btn" onClick={() => void chargerPlus()} disabled={suiteEnCours}>
                {suiteEnCours ? 'Chargement…' : 'Charger plus'}
              </button>
            ) : null}
          </div>
        </>
      ) : null}
    </>
  )
}

/* ═══════════════════════ filtres de catalogue ═══════════════════════ */

const PLUSIEURS = '__plusieurs'

/**
 * Un filtre à valeurs de catalogue. L'API accepte plusieurs valeurs (un lien
 * partagé peut en porter) ; le sélecteur en pose une, et dit « Plusieurs »
 * quand l'adresse en apporte davantage.
 */
function FiltreCatalogue(props: {
  label: string
  valeurs: string[]
  options: ReadonlyArray<{ code: string; label: string; tone?: string }>
  onChange: (v: string[]) => void
}) {
  const options: SelectOption[] = [
    { value: '', label: 'Tous' },
    ...props.options.map((o) => ({ value: o.code, label: o.label })),
  ]
  const valeur = props.valeurs.length === 0 ? '' : props.valeurs.length === 1 ? props.valeurs[0]! : PLUSIEURS
  return (
    <Select label={props.label} value={valeur} options={options} compact
      placeholder={`Plusieurs (${props.valeurs.length})`} ariaLabel={`Filtrer par ${props.label.toLowerCase()}`}
      onChange={(v) => props.onChange(v ? [v] : [])} />
  )
}

function FiltreResponsableSelect(props: {
  valeur: FiltreResponsable
  moi: string | null
  responsables: Responsable[]
  onChange: (v: FiltreResponsable) => void
}) {
  const options: SelectOption[] = [
    { value: 'tous', label: 'Tous' },
    { value: 'non_affecte', label: 'Non affectés' },
    { value: 'affecte', label: 'Affectés' },
  ]
  if (props.moi) options.push({ value: `p:${props.moi}`, label: 'Affectés à moi' })
  for (const r of props.responsables) {
    if (r.id !== props.moi) options.push({ value: `p:${r.id}`, label: r.display_name })
  }
  const v = props.valeur.type === 'personne' ? `p:${props.valeur.id}` : props.valeur.type
  return (
    <Select label="Responsable" value={v} options={options} compact placeholder="Une personne choisie"
      ariaLabel="Filtrer par responsable"
      onChange={(x) => props.onChange(x.startsWith('p:') ? { type: 'personne', id: x.slice(2) }
        : x === 'non_affecte' ? { type: 'non_affecte' } : x === 'affecte' ? { type: 'affecte' } : { type: 'tous' })} />
  )
}

/* ═══════════════════════ table (bureau) et cartes (téléphone) ═══════════════════════ */

function Table({ items, meta }: { items: Resume[]; meta: MetaSignalements }) {
  return (
    <div className="lc-panel sig-table-zone">
      <table className="lc-table sig-table">
        <thead>
          <tr>
            <th scope="col">Référence</th>
            <th scope="col">Titre</th>
            <th scope="col">Statut</th>
            <th scope="col">Priorité</th>
            <th scope="col" className="sig-col--large">Impact</th>
            <th scope="col" className="sig-col--large">Catégorie</th>
            <th scope="col" className="sig-col--large">Emplacement</th>
            <th scope="col">Responsable</th>
            <th scope="col">Mise à jour</th>
          </tr>
        </thead>
        <tbody>
          {items.map((s) => (
            <tr key={s.id} className="sig-ligne" onClick={(e) => {
              // La ligne entière ouvre la fiche à la souris ; le clavier passe par le lien du titre.
              if ((e.target as HTMLElement).closest('a, button')) return
              navigate(cheminFiche(s.reference))
            }}>
              <td><span className="sig-ref">{s.reference}</span></td>
              <td className="sig-table__titre">
                <Link href={cheminFiche(s.reference)} className="sig-lien sig-lien--titre">{s.titre}</Link>
                <span className="sig-table__sous">
                  {libelle(meta.categories, s.categorie)}{s.emplacement ? ` · ${s.emplacement}` : ''} · {libelle(meta.impacts, s.impact)}
                </span>
              </td>
              <td><Pastille ton={tonDe(meta.statuts, s.statut)} label={libelle(meta.statuts, s.statut)} /></td>
              <td><Pastille ton={tonDe(meta.priorites, s.priorite)} label={libelle(meta.priorites, s.priorite)} /></td>
              <td className="sig-col--large"><Pastille ton={tonDe(meta.impacts, s.impact)} label={libelle(meta.impacts, s.impact)} /></td>
              <td className="sig-col--large">{libelle(meta.categories, s.categorie)}</td>
              <td className="sig-col--large">{s.emplacement || <span className="sig-manque">—</span>}</td>
              <td>{s.responsable ? nomDe(s.responsable) : <span className="sig-manque">Non affecté</span>}</td>
              <td><span title={dateHeureParis(s.updated_at)}>{ilYA(s.updated_at)}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function Cartes({ items, meta }: { items: Resume[]; meta: MetaSignalements }) {
  return (
    <ul className="sig-cartes">
      {items.map((s) => (
        <li key={s.id}>
          <Link href={cheminFiche(s.reference)} className="sig-carte">
            <span className="sig-carte__tete">
              <span className="sig-ref">{s.reference}</span>
              <span title={dateHeureParis(s.updated_at)} className="sig-aide">{ilYA(s.updated_at)}</span>
            </span>
            <span className="sig-carte__titre">{s.titre}</span>
            <span className="sig-carte__pastilles">
              <Pastille ton={tonDe(meta.statuts, s.statut)} label={libelle(meta.statuts, s.statut)} />
              <Pastille ton={tonDe(meta.priorites, s.priorite)} label={libelle(meta.priorites, s.priorite)} />
              <Pastille ton={tonDe(meta.impacts, s.impact)} label={libelle(meta.impacts, s.impact)} />
            </span>
            <span className="sig-carte__meta">
              {libelle(meta.categories, s.categorie)}{s.emplacement ? ` · ${s.emplacement}` : ''}
              {' · '}{s.responsable ? nomDe(s.responsable) : 'Non affecté'}
            </span>
          </Link>
        </li>
      ))}
    </ul>
  )
}

/** Les filtres d'une adresse, pour qui ouvre la liste depuis ailleurs (fiche, hub). */
export function adresseListe(f: Partial<Filtres>): string {
  const q = filtresVersUrl({ ...FILTRES_DEFAUT, ...f }).toString()
  return q ? `${CHEMIN}?${q}` : CHEMIN
}
